import { useState } from "react";
import { arrayMove } from '@dnd-kit/sortable';
import LinksService from '../services/links.service';

export const useLinks = () => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);

    // Form: nuevo link
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [hasSubLinks, setHasSubLinks] = useState(false);
    // Filas de sub-links a crear junto con el link padre
    const [subLinksToCreate, setSubLinksToCreate] = useState([{ title: '', url: '' }]);

    // Estado de expansión en la lista
    const [expandedLinks, setExpandedLinks] = useState({});

    // Dialog de edición
    const [editDialog, setEditDialog] = useState({ open: false, item: null, type: null, parentId: null });
    const [editTitle, setEditTitle] = useState('');
    const [editUrl, setEditUrl] = useState('');
    const [editIsActive, setEditIsActive] = useState(true);

    // Dialog para agregar sub-link a link existente
    const [addSubDialog, setAddSubDialog] = useState({ open: false, parentId: null, parentTitle: '' });
    const [newSubTitle, setNewSubTitle] = useState('');
    const [newSubUrl, setNewSubUrl] = useState('');

    const getAllLinks = async () => {
        try {
            setLoading(true);
            const response = await LinksService.getAllLinks();
            setLinks(response.data || []);
        } catch (error) {
            console.error("Error al obtener links:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Gestión de filas de sub-links en el form de creación ---
    const addSubLinkRow = () => {
        setSubLinksToCreate(prev => [...prev, { title: '', url: '' }]);
    };

    const removeSubLinkRow = (index) => {
        setSubLinksToCreate(prev => prev.filter((_, i) => i !== index));
    };

    const updateSubLinkRow = (index, field, value) => {
        setSubLinksToCreate(prev =>
            prev.map((row, i) => i === index ? { ...row, [field]: value } : row)
        );
    };

    // --- Crear link (y sus sub-links si corresponde) ---
    const handleCreateLink = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            alert('El título es obligatorio');
            return;
        }
        if (!hasSubLinks && !url.trim()) {
            alert('La URL es obligatoria');
            return;
        }
        if (hasSubLinks && subLinksToCreate.some(s => !s.title.trim() || !s.url.trim())) {
            alert('Completá el título y la URL de todos los sub-links');
            return;
        }

        try {
            setLoading(true);
            const response = await LinksService.createLink({
                title,
                url: hasSubLinks ? null : url,
                hasSubLinks,
                isActive: true,
            });

            if (hasSubLinks) {
                const parentId = response?.data?.id ?? response?.id;
                for (const sub of subLinksToCreate) {
                    await LinksService.createSubLink(parentId, {
                        title: sub.title,
                        url: sub.url,
                        isActive: true,
                    });
                }
            }

            setTitle('');
            setUrl('');
            setHasSubLinks(false);
            setSubLinksToCreate([{ title: '', url: '' }]);
            await getAllLinks();
        } catch (error) {
            console.error("Error al crear link:", error);
            alert('No se pudo crear el link');
        } finally {
            setLoading(false);
        }
    };

    // --- Dialog: agregar sub-link a link existente ---
    const openAddSubDialog = (parentId, parentTitle) => {
        setAddSubDialog({ open: true, parentId, parentTitle });
        setNewSubTitle('');
        setNewSubUrl('');
    };

    const closeAddSubDialog = () => {
        setAddSubDialog({ open: false, parentId: null, parentTitle: '' });
    };

    const handleCreateSubLink = async () => {
        if (!newSubTitle.trim()) {
            alert('El título es obligatorio');
            return;
        }
        if (!newSubUrl.trim()) {
            alert('La URL es obligatoria');
            return;
        }
        try {
            setLoading(true);
            await LinksService.createSubLink(addSubDialog.parentId, {
                title: newSubTitle,
                url: newSubUrl,
                isActive: true,
            });
            closeAddSubDialog();
            await getAllLinks();
        } catch (error) {
            console.error("Error al crear sub-link:", error);
            alert('No se pudo crear el sub-link');
        } finally {
            setLoading(false);
        }
    };

    // --- Toggle status ---
    const handleToggleStatus = async (id, isSubLink = false, parentId = null) => {
        try {
            if (isSubLink) {
                await LinksService.toggleSubLinkStatus(parentId, id);
            } else {
                await LinksService.toggleLinkStatus(id);
            }
            await getAllLinks();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            alert('No se pudo cambiar el estado');
        }
    };

    // --- Archivar ---
    const handleArchive = async (id, isSubLink = false, parentId = null) => {
        const confirmed = window.confirm('¿Archivar este elemento? No se mostrará más en la app.');
        if (!confirmed) return;
        try {
            setLoading(true);
            if (isSubLink) {
                await LinksService.archiveSubLink(parentId, id);
            } else {
                await LinksService.archiveLink(id);
            }
            await getAllLinks();
        } catch (error) {
            console.error('Error al archivar:', error);
            alert('No se pudo archivar');
        } finally {
            setLoading(false);
        }
    };

    // --- Dialog edición ---
    const openEditDialog = (item, type, parentId = null) => {
        setEditDialog({ open: true, item, type, parentId });
        setEditTitle(item.title);
        setEditUrl(item.url || '');
        setEditIsActive(item.isActive ?? true);
    };

    const closeEditDialog = () => {
        setEditDialog({ open: false, item: null, type: null, parentId: null });
    };

    const handleSaveEdit = async () => {
        const { item, type, parentId } = editDialog;
        if (!editTitle.trim()) {
            alert('El título es obligatorio');
            return;
        }
        try {
            setLoading(true);
            const data = { title: editTitle, url: editUrl || null, isActive: editIsActive };
            if (type === 'link') {
                await LinksService.updateLink(item.id, data);
            } else {
                await LinksService.updateSubLink(parentId, item.id, data);
            }
            closeEditDialog();
            await getAllLinks();
        } catch (error) {
            console.error('Error al guardar:', error);
            alert('No se pudo guardar los cambios');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (id) => {
        setExpandedLinks(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- Drag and drop reorder ---
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = links.findIndex(l => l.id === active.id);
        const newIndex = links.findIndex(l => l.id === over.id);
        const reordered = arrayMove(links, oldIndex, newIndex);

        // Snapshot para revertir si falla
        const previous = links;

        // Actualización optimista
        setLinks(reordered);

        try {
            // Solo actualizamos los links cuyo índice cambió
            const updates = reordered
                .map((link, index) => ({ link, index, oldIndex: previous.findIndex(l => l.id === link.id) }))
                .filter(({ index, oldIndex: oi }) => index !== oi)
                .map(({ link, index }) => LinksService.reorderLink(link.id, index));

            await Promise.all(updates);
        } catch (err) {
            console.error('Error al guardar el orden:', err);
            setLinks(previous);
        }
    };

    return {
        links,
        loading,
        title,
        url,
        hasSubLinks,
        subLinksToCreate,
        editDialog,
        editTitle,
        editUrl,
        editIsActive,
        expandedLinks,
        addSubDialog,
        newSubTitle,
        newSubUrl,
        setTitle,
        setUrl,
        setHasSubLinks,
        setEditTitle,
        setEditUrl,
        setEditIsActive,
        setNewSubTitle,
        setNewSubUrl,
        getAllLinks,
        handleCreateLink,
        handleCreateSubLink,
        handleToggleStatus,
        handleArchive,
        openEditDialog,
        closeEditDialog,
        handleSaveEdit,
        toggleExpand,
        addSubLinkRow,
        removeSubLinkRow,
        updateSubLinkRow,
        openAddSubDialog,
        closeAddSubDialog,
        handleDragEnd,
    };
}
